import { EHttpService } from '~/models/api'
import EEnvironmentVariables from '~/models/config'
import AmplifyHttpService from '~/services/http/amplifyHttpService'
import AxiosHttpService from '~/services/http/axiosHttpService'
import HttpServiceBase, { HttpRequestBase } from '~/services/http/models'
import SigV4HttpService from '~/services/http/sig4HttpService'

class HttpService {
  private static partnerServiceInstance = new AxiosHttpService(
    EHttpService.partner,
    import.meta.env[EEnvironmentVariables.VITE_PARTNER_SERVICE] || '',
  )

  private static scenariosServiceInstance = new AxiosHttpService(
    EHttpService.scenarios,
    import.meta.env[EEnvironmentVariables.VITE_SCENARIO_SERVICE] || '',
  )

  // HEY-5398 auth cleanup: routed through the shared private-ALB gateway
  // (same origin as internalServiceInstance below) instead of each
  // service's own public ALB. VITE_TEXT_SERVICE is no longer read — remove
  // it from deploy config once this is verified.
  private static agentServiceInstance = new AxiosHttpService(
    EHttpService.agent,
    import.meta.env[EEnvironmentVariables.VITE_INTERNAL_API_SERVICE] || '',
    '/api/agent',
  )

  // agent-service's /guest/agents* routes are NOT dual-mounted behind the
  // gateway (unauthenticated chat-widget lookups must never hit the JWT
  // authorizer) — stays on the old public CoreALB. VITE_AGENT_SERVICE is
  // still read, for this one instance only.
  //
  // Lazily constructed (unlike every other instance above/below): a missing
  // VITE_AGENT_SERVICE must only break guest-chat-widget lookups, not throw
  // during module evaluation and take down the entire bundle — login,
  // admin, everything else imports this same HttpService class.
  private static guestAgentServiceInstance: AxiosHttpService | null = null

  private static getGuestAgentServiceInstance(): AxiosHttpService {
    if (!this.guestAgentServiceInstance) {
      this.guestAgentServiceInstance = new AxiosHttpService(
        EHttpService.guestAgent,
        import.meta.env[EEnvironmentVariables.VITE_AGENT_SERVICE] || '',
      )
    }
    return this.guestAgentServiceInstance
  }

  private static textServiceInstance = new AxiosHttpService(
    EHttpService.text,
    import.meta.env[EEnvironmentVariables.VITE_INTERNAL_API_SERVICE] || '',
    '/api/text',
  )

  private static internalServiceInstanceV4 = new SigV4HttpService(
    EHttpService.internalV4,
    import.meta.env[EEnvironmentVariables.VITE_INTERNAL_API_SERVICE] || '',
  )

  private static internalServiceInstance = new AxiosHttpService(
    EHttpService.internal,
    import.meta.env[EEnvironmentVariables.VITE_INTERNAL_API_SERVICE] || '',
  )

  // private static voipServiceInstance = new AxiosHttpService(
  //   EHttpService.voip,
  //   import.meta.env[EEnvironmentVariables.VITE_VOIP_SERVICE] || '',
  // )

  private static amplifyServiceInstance: HttpServiceBase =
    new AmplifyHttpService()

  private static async getInstance(
    service: EHttpService,
  ): Promise<HttpServiceBase | AxiosHttpService> {
    switch (service) {
      case EHttpService.partner:
        return this.partnerServiceInstance

      case EHttpService.scenarios:
        return this.scenariosServiceInstance

      case EHttpService.agent:
        return this.agentServiceInstance

      case EHttpService.guestAgent:
        return this.getGuestAgentServiceInstance()

      case EHttpService.text:
        return this.textServiceInstance

      // case EHttpService.voip:
      //   return this.voipServiceInstance

      case EHttpService.internal:
        return this.internalServiceInstance

      case EHttpService.internalV4:
        return this.internalServiceInstanceV4

      default:
        return this.amplifyServiceInstance
    }
  }

  public static async getAllAvailableInterceptors() {
    // guestAgentServiceInstance deliberately excluded: it's lazily
    // constructed (see above) specifically so a missing VITE_AGENT_SERVICE
    // doesn't crash app-wide, and forcing it here — unconditionally, on
    // every page load regardless of whether a guest widget is even in use —
    // would defeat that. It also serves only unauthenticated guest lookups,
    // so the sign-out-on-401 interceptor this wires up doesn't apply to it.
    return Promise.all([
      this.partnerServiceInstance,
      this.scenariosServiceInstance,
      this.agentServiceInstance,
      this.textServiceInstance,
      // this.voipServiceInstance,
      this.internalServiceInstance,
    ]).then((instances) => {
      return instances.map((instance) => instance.getInterceptors())
    })
  }

  public static async get<T>(
    { path, query, headers, signal }: HttpRequestBase,
    service: EHttpService = EHttpService.amplify,
  ) {
    return (await HttpService.getInstance(service)).get<T>({
      path,
      query,
      headers,
      signal,
    })
  }

  /** Binary GET (PDF, etc.). Only Axios-backed services support this. */
  public static async getBlob(
    request: HttpRequestBase,
    service: EHttpService = EHttpService.amplify,
  ): Promise<Blob> {
    const instance = await HttpService.getInstance(service)
    if (!(instance instanceof AxiosHttpService)) {
      throw new Error('Binary GET is only supported for Axios-backed services')
    }
    return instance.getBlob(request)
  }

  public static async post<T>(
    { path, query, payload, onUploadProgress, headers }: HttpRequestBase,
    service: EHttpService = EHttpService.amplify,
  ) {
    return (await HttpService.getInstance(service)).post<T>({
      path,
      query,
      payload,
      onUploadProgress,
      headers,
    })
  }

  public static async put<T>(
    { path, query, payload, headers }: HttpRequestBase,
    service: EHttpService = EHttpService.amplify,
  ) {
    return (await HttpService.getInstance(service)).put<T>({
      path,
      query,
      payload,
      headers,
    })
  }

  public static async patch<T>(
    { path, query, payload, headers }: HttpRequestBase,
    service: EHttpService = EHttpService.amplify,
  ) {
    return (await HttpService.getInstance(service)).patch<T>({
      path,
      query,
      payload,
      headers,
    })
  }

  public static async delete<T>(
    { path, query, payload, headers }: HttpRequestBase,
    service: EHttpService = EHttpService.amplify,
  ) {
    return (await HttpService.getInstance(service)).delete<T>({
      path,
      query,
      payload,
      headers,
    })
  }
}

export default HttpService
